import React from 'react';
import { Head } from 'vite-react-ssg';
import { useLocation } from 'react-router-dom';
import { SITE, absUrl, organizationSchema, websiteSchema, breadcrumbSchema, faqSchema } from '../lib/site';

/**
 * One component for every page: title, description, canonical, Open Graph, Twitter,
 * robots directives and JSON-LD (Organization + WebSite on every page; breadcrumbs, FAQ
 * and anything in `schema` on top).
 *
 * Props: title, description, keywords, ogImage, schema (object | array),
 *        type ('website' | 'article'), article {published, modified, tags, section},
 *        breadcrumbs [{ name, path }], faq [{ q, a }], noindex
 */
const SEO = ({ title, description, keywords, ogImage, schema, type = 'website', article, breadcrumbs, faq, noindex }) => {
  const location = useLocation();
  // one URL per page: no trailing slash (matches the sitemap), root stays "/"
  const currentUrl = absUrl(location.pathname);
  const image = ogImage || SITE.logo;

  const graph = [organizationSchema(), websiteSchema()];
  if (breadcrumbs?.length) graph.push(breadcrumbSchema(breadcrumbs));
  if (faq?.length) graph.push(faqSchema(faq));
  const extra = Array.isArray(schema) ? schema : schema && Object.keys(schema).length ? [schema] : [];
  const jsonLd = [...graph, ...extra];

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'}
      />
      <meta name="author" content={SITE.name} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content="en_IN" />
      {type === 'article' && article?.published && <meta property="article:published_time" content={article.published} />}
      {type === 'article' && article?.modified && <meta property="article:modified_time" content={article.modified} />}
      {type === 'article' && article?.section && <meta property="article:section" content={article.section} />}
      {type === 'article' && article?.tags?.map((t) => <meta key={t} property="article:tag" content={t} />)}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <link rel="canonical" href={currentUrl} />
      <link rel="alternate" type="application/rss+xml" title={`${SITE.name} blog`} href={`${SITE.url}/rss.xml`} />

      {jsonLd.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Head>
  );
};

export default SEO;
